import {
  Component,
  HostListener,
  OnInit,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';

export interface SearchablePage {
  title: string;
  path: string;
  category: string;
  description: string;
  keywords: string[];
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {

  isScrolled = false;
  mobileMenuOpen = false;
  activeAccordion: string | null = null;

  // ── Home-only elements ─────────────────────────────────
  isHome = false;
  showApplyButton = true;
  searchOpen = false;
  searchQuery = '';
  isHindi = false;

  searchResults: SearchablePage[] = [];

  pages: SearchablePage[] = [
    // ABOUT US
    { title: 'Governing Body', path: '/about-us/governing-body', category: 'About Us', description: 'Meet the executive trustees and managing committee members steering our institution.', keywords: ['governing', 'body', 'trustees', 'management', 'committee'] },
    { title: 'President Message', path: '/about-us/president-message', category: 'About Us', description: 'Read the message from our President.', keywords: ['president', 'message', 'welcome', 'address', 'leader'] },
    { title: 'Principal Message', path: '/about-us/principal-message', category: 'About Us', description: 'Read the message from our Principal.', keywords: ['principal', 'message', 'welcome', 'address', 'leader', 'desk'] },

    // AISHE
    { title: 'AISHE 2018', path: 'assets/pdf/aishe/2018.pdf', category: 'AISHE', description: 'AISHE Report and Certifications for the academic year 2018.', keywords: ['aishe', '2018', 'accreditation', 'report'] },
    { title: 'AISHE 2019', path: 'assets/pdf/aishe/2019.pdf', category: 'AISHE', description: 'AISHE Report and Certifications for the academic year 2019.', keywords: ['aishe', '2019', 'accreditation', 'report'] },
    { title: 'AISHE 2020', path: 'assets/pdf/aishe/2020.pdf', category: 'AISHE', description: 'AISHE Report and Certifications for the academic year 2020.', keywords: ['aishe', '2020', 'accreditation', 'report'] },
    { title: 'AISHE 2021', path: 'assets/pdf/aishe/2021.pdf', category: 'AISHE', description: 'AISHE Report and Certifications for the academic year 2021.', keywords: ['aishe', '2021', 'accreditation', 'report'] },
    { title: 'AISHE 2022', path: 'assets/pdf/aishe/2022.pdf', category: 'AISHE', description: 'AISHE Report and Certifications for the academic year 2022.', keywords: ['aishe', '2022', 'accreditation', 'report'] },

    // ACADEMIC
    { title: 'Courses', path: '/academic/courses', category: 'Academic', description: 'Explore various courses and academic options available.', keywords: ['courses', 'academic', 'programs', 'subjects'] },
    { title: 'Result', path: 'https://new.kuk.ac.in/results/', category: 'Academic', description: 'Check examinations results and performance stats.', keywords: ['result', 'exam', 'marks', 'performance'] },
    { title: 'Fee Structure', path: '', category: 'Academic', description: 'Check admission and academic fee structures.', keywords: ['fee', 'structure', 'charges', 'payments'] },

    // COLLEGE STAFF
    { title: 'College Staff', path: '/college-staff', category: 'College Staff', description: 'Meet the faculty and non-teaching staff at Vaish Girls College.', keywords: ['staff', 'teachers', 'faculty', 'teaching', 'non-teaching'] },

    // CAMPUS
    { title: 'NSS', path: '/campus/nss', category: 'Campus', description: 'Explore the National Service Scheme activities and updates.', keywords: ['nss', 'camp', 'service', 'community'] },
    { title: 'Women Cell', path: '/campus/women-cell', category: 'Campus', description: 'Activities and support from our Women Empowerment Cell.', keywords: ['women', 'cell', 'empowerment', 'safety'] },
    { title: 'Anti-Raging', path: '/campus/anti-raging', category: 'Campus', description: 'Anti-Ragging directives, policies, and grievance helpline.', keywords: ['anti-ragging', 'ragging', 'complaint', 'safety', 'raging'] },

    // ADMISSION
    { title: 'Terms & Norms', path: '/admission/terms-norms', category: 'Admission', description: 'Understand terms and conditions and regulatory norms for admission.', keywords: ['terms', 'norms', 'rules', 'regulations'] },
    { title: 'Admission Dates', path: 'assets/pdf/admission/admission-guideline-2025.pdf', category: 'Admission', description: 'Key dates, timelines, and registration schedules for admissions.', keywords: ['dates', 'admission', 'timeline', 'schedule'] },
    { title: 'Admission Schedule UG', path: 'assets/pdf/admission/admission-schedule-ug-25-26.pdf', category: 'Admission', description: 'Registration schedules and cutoff details for undergraduate admissions.', keywords: ['schedule', 'ug', 'undergraduate', 'admission'] },

    // CONTACT US
    { title: 'Contact Us', path: '/contact-us', category: 'Contact', description: 'Reach us via email, phone, or visit our campus in Samalkha.', keywords: ['contact', 'address', 'phone', 'email', 'map', 'location'] }
  ];

  private isMobile = false;
  private routerSub!: Subscription;

  constructor(private renderer: Renderer2, private router: Router) { }

  // ─────────────────────────────────────────────────────────
  //  LIFECYCLE
  // ─────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.checkBreakpoint();
    this.initTranslateState();
    this.trackRoute();
  }

  ngOnDestroy(): void {
    this.renderer.removeStyle(document.body, 'overflow');
    this.routerSub?.unsubscribe();
  }

  // ─────────────────────────────────────────────────────────
  //  SCROLL
  // ─────────────────────────────────────────────────────────

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    this.isScrolled = window.scrollY > 60;
  }

  // ─────────────────────────────────────────────────────────
  //  RESIZE
  // ─────────────────────────────────────────────────────────

  @HostListener('window:resize', [])
  onWindowResize(): void {
    this.checkBreakpoint();
    if (!this.isMobile && this.mobileMenuOpen) {
      this.closeMobileMenu();
    }
  }

  private checkBreakpoint(): void {
    this.isMobile = window.innerWidth <= 1024;
  }

  // ─────────────────────────────────────────────────────────
  //  OUTSIDE CLICK
  // ─────────────────────────────────────────────────────────

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.mobileMenuOpen) return;

    const target = event.target as HTMLElement;
    const insideDrawer = !!target.closest('.navbar-collapse');
    const onHamburger = !!target.closest('.navbar-toggler');

    if (!insideDrawer && !onHamburger) {
      this.closeMobileMenu();
    }
  }

  // ─────────────────────────────────────────────────────────
  //  ESCAPE KEY
  // ─────────────────────────────────────────────────────────

  @HostListener('document:keydown.escape', [])
  onEscapeKey(): void {
    if (this.mobileMenuOpen) {
      this.closeMobileMenu();
    }
  }

  // ─────────────────────────────────────────────────────────
  //  HAMBURGER
  // ─────────────────────────────────────────────────────────

  toggleMobileMenu(event: Event): void {
    event.stopPropagation();
    this.mobileMenuOpen ? this.closeMobileMenu() : this.openMobileMenu();
  }

  private openMobileMenu(): void {
    this.mobileMenuOpen = true;
    this.renderer.setStyle(document.body, 'overflow', 'hidden');
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen = false;
    this.activeAccordion = null;
    this.renderer.removeStyle(document.body, 'overflow');
  }

  // ─────────────────────────────────────────────────────────
  //  ACCORDION
  // ─────────────────────────────────────────────────────────

  toggleAccordion(key: string, event: Event): void {
    event.stopPropagation();
    if (!this.isMobile) return;
    this.activeAccordion = this.activeAccordion === key ? null : key;
  }

  isAccordionOpen(key: string): boolean {
    return this.activeAccordion === key;
  }

  // ─────────────────────────────────────────────────────────
  //  ROUTE TRACKING (home-only elements)
  // ─────────────────────────────────────────────────────────

  private trackRoute(): void {
    const url = this.router.url;
    this.isHome = url === '/' || url === '';
    this.showApplyButton = url !== '/apply-online';

    this.routerSub = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: any) => {
        const u: string = e.urlAfterRedirects ?? '';
        this.isHome = u === '/' || u === '';
        this.showApplyButton = u !== '/apply-online';
        this.closeDropdowns();
      });
  }

  private closeDropdowns(): void {
    (document.activeElement as HTMLElement)?.blur?.();

    const openToggles = document.querySelectorAll<HTMLElement>(
      '#main-header .nav-item.dropdown > .nav-link'
    );
    openToggles.forEach(el => {
      el.dispatchEvent(new MouseEvent('mouseleave', { bubbles: true }));
    });

    const nav = document.querySelector<HTMLElement>('#main-header .main-nav');
    if (nav) {
      nav.style.pointerEvents = 'none';
      requestAnimationFrame(() => {
        nav.style.pointerEvents = '';
      });
    }
  }

  // ─────────────────────────────────────────────────────────
  //  GOOGLE TRANSLATE
  // ─────────────────────────────────────────────────────────

  private initTranslateState(): void {
    this.isHindi = this.getCookieValue('googtrans') === '/en/hi';
  }

  triggerTranslate(): void {
    const next = this.isHindi ? '/en/en' : '/en/hi';
    this.setCookie('googtrans', next);
    this.isHindi = !this.isHindi;
    window.location.reload();
  }

  private getCookieValue(name: string): string {
    const match = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${name}=`));
    return match ? match.split('=').slice(1).join('=') : '';
  }

  private setCookie(name: string, value: string): void {
    const base = `${name}=${value}; path=/`;
    document.cookie = base;
    document.cookie = `${base}; domain=${location.hostname}`;
  }

  // ─────────────────────────────────────────────────────────
  //  SEARCH
  // ─────────────────────────────────────────────────────────

  toggleSearch(): void {
    this.searchOpen = !this.searchOpen;
    if (this.searchOpen) {
      setTimeout(() => {
        const input = document.querySelector('.search-input') as HTMLInputElement | null;
        input?.focus();
      }, 150);
    } else {
      this.searchQuery = '';
      this.searchResults = [];
    }
  }

  closeSearch(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.classList.contains('search-overlay')) {
      this.searchOpen = false;
      this.searchQuery = '';
      this.searchResults = [];
    }
  }

  closeSearchDirectly(): void {
    this.searchOpen = false;
    this.searchQuery = '';
    this.searchResults = [];
  }

  onSearchChange(): void {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) {
      this.searchResults = [];
      return;
    }
    this.searchResults = this.pages.filter(page =>
      page.title.toLowerCase().includes(query) ||
      page.description.toLowerCase().includes(query) ||
      page.category.toLowerCase().includes(query) ||
      page.keywords.some(kw => kw.toLowerCase().includes(query))
    );
  }

  doSearch(): void {
    this.onSearchChange();
  }
}
